<script setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import PaymentPayerCard from "@/components/payments/PaymentPayerCard.vue";
import PaymentReceiptCard from "@/components/payments/PaymentReceiptCard.vue";
import PaymentInfoCard from "@/components/payments/PaymentInfoCard.vue";
import PaymentTimeline from "@/components/payments/PaymentTimeline.vue";
import PaymentEditModal from "@/components/payments/PaymentEditModal.vue";
import { usePaymentStore } from "@/stores/payment";
import { usePrint } from "@/composables/usePrint";

const route = useRoute();
const router = useRouter();
const paymentStore = usePaymentStore();
const paymentEditModal = ref(null);

const paymentId = computed(() => route.params.id);
const payment = computed(() => paymentStore.getPaymentById(paymentId.value));
const timelineItems = computed(() =>
  paymentStore.getEventsByPaymentId(paymentId.value),
);

const goBack = () => {
  router.push("/payments");
};

const handleEdit = () => paymentEditModal.value?.open(payment.value);

const { printReceipt } = usePrint();
const handlePrint = () => printReceipt(payment.value);
</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <div v-if="payment" class="vstack gap-4">
        <div
          class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
        >
          <div>
            <button
              type="button"
              class="btn btn-secondary border rounded-3 mb-3"
              @click="goBack"
            >
              <i class="bi bi-arrow-left me-1"></i>กลับไปรายการรับชำระเงิน
            </button>

            <div class="d-flex align-items-center gap-3 flex-wrap">
              <div class="payment-icon">
                <i class="bi bi-cash-coin"></i>
              </div>
              <div>
                <h3 class="fw-bold mb-1">{{ payment.paymentNo }}</h3>
                <div class="text-muted">
                  {{ payment.invoiceNo }} • {{ payment.residentName }} •
                  {{ payment.unitCode }}
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 flex-wrap">
            <button
              type="button"
              class="btn btn-secondary rounded-3"
              @click="handlePrint"
            >
              <i class="bi bi-printer me-1"></i>พิมพ์ใบเสร็จ
            </button>
            <button
              type="button"
              class="btn btn-primary rounded-3"
              @click="handleEdit"
            >
              <i class="bi bi-pencil-square me-1"></i>แก้ไขใบเสร็จ
            </button>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-xl-4">
            <PaymentPayerCard :payment="payment" />
          </div>
          <div class="col-xl-4">
            <PaymentReceiptCard :payment="payment"  @print="handlePrint" />
          </div>
          <div class="col-xl-4">
            <PaymentInfoCard :payment="payment" />
          </div>
        </div>

        <PaymentTimeline :items="timelineItems" />
      </div>

      <div v-else class="card border-0 shadow-sm rounded-4">
        <div class="card-body p-5 text-center">
          <i class="bi bi-search fs-1 text-secondary d-block mb-3"></i>
          <h4 class="fw-bold mb-2">ไม่พบข้อมูลรายการรับชำระ</h4>
          <p class="text-muted mb-4">
            อาจเป็นเพราะลิงก์ไม่ถูกต้อง หรือยังไม่มีข้อมูลรายการนี้ในระบบ
          </p>
          <button
            type="button"
            class="btn btn-primary rounded-3 px-4"
            @click="goBack"
          >
            <i class="bi bi-arrow-left me-2"></i>
            กลับไปหน้ารายการรับชำระเงิน
          </button>
        </div>
      </div>
    </div>
    <PaymentEditModal ref="paymentEditModal"  />
  </MainLayout>
</template>

<style scoped>
.payment-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}
</style>
